import Button from '@cobliteam/ui/components/Button';
import Dialog from '@cobliteam/ui/components/Dialog';
import { useGuide } from '@knocklabs/react';
import { useEffect } from 'react';

/**
 * Schema de conteúdo do message type `modal` nativo do Knock.
 *
 * É um contrato com o message type definido no dashboard do Knock — se um campo for
 * adicionado/renomeado lá, espelhe aqui para que `step.content.<campo>` continue tipado.
 * https://docs.knock.app/in-app-ui/guides/render-guides
 */
type KnockModalContent = {
  title?: string;
  body?: string;
  primary_button?: { text?: string; action?: string };
  secondary_button?: { text?: string; action?: string };
};

/**
 * Renderiza o próximo guide elegível do message type `modal` como um Dialog do design system.
 * Regras de elegibilidade, targeting e ativação são avaliadas no servidor pelo
 * `KnockGuideProvider`; este componente apenas renderiza o que o hook retorna e reporta o
 * engajamento (seen / interacted / archived) de volta ao Knock.
 *
 * Montado globalmente pelo `AppLayout`, condicionado ao Knock estar configurado (para que
 * `useGuide` nunca seja chamado fora do `KnockGuideProvider`).
 */
const KnockGuideModal = () => {
  const { step } = useGuide<KnockModalContent>({ type: 'modal' });

  useEffect(() => {
    // Impressão: dispara uma vez por guide buscado. Protegido porque o effect ainda roda
    // quando o usuário é inelegível e o hook não retorna step.
    if (step) step.markAsSeen();
  }, [step]);

  if (!step) return null;

  const { title, body, primary_button: primaryButton, secondary_button: secondaryButton } = step.content;

  // Fechar pelo X, pelo overlay, Esc ou pela ação secundária conta como dispensa:
  // arquiva para o Knock parar de retornar este guide para o usuário.
  const handleOpenChange = (open: boolean) => {
    if (!open) step.markAsArchived();
  };

  const handlePrimary = () => {
    step.markAsInteracted();
    if (primaryButton?.action) window.open(primaryButton.action, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <Dialog.Content size='sm'>
        <Dialog.Header>
          {title ? <Dialog.Title>{title}</Dialog.Title> : null}
          <Dialog.Close />
        </Dialog.Header>
        {body ? <Dialog.Body>{body}</Dialog.Body> : null}
        {primaryButton?.text || secondaryButton?.text ? (
          <Dialog.Footer>
            {secondaryButton?.text ? (
              <Dialog.Close asChild>
                <Button variant='outlined' color='default'>
                  {secondaryButton.text}
                </Button>
              </Dialog.Close>
            ) : null}
            {primaryButton?.text ? <Button onClick={handlePrimary}>{primaryButton.text}</Button> : null}
          </Dialog.Footer>
        ) : null}
      </Dialog.Content>
    </Dialog>
  );
};

export default KnockGuideModal;
